import React, { useState, useEffect } from 'react';
import NineBoxChart from '../components/NineBoxChart';
import RadarCompetenciasChart from '../components/RadarCompetenciasChart';
import HeatmapChart from '../components/HeatmapChart';
import DistribuicaoScarfChart from '../components/DistribuicaoScarfChart';
import AlertasCriticosTable from '../components/AlertasCriticosTable';

const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1', // Usar variável de ambiente com fallback
  endpoints: {
    origem: '/origem',
    pessoas: '/pessoas',
    nineBox: '/nine-box',
    radarCompetencias: '/radar-competencias',
    heatmapCompetencias: '/heatmap-competencias',
    distribuicaoScarf: '/distribuicao-scarf',
    alertasCriticos: '/alertas-criticos',
  }
};

const OverviewDashboard = ({ apiCall, activeTab }) => {
  // Estados para filtros da Visão Geral
  const [origens, setOrigens] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [selectedOrigem, setSelectedOrigem] = useState('');
  const [selectedPessoa, setSelectedPessoa] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Estados para dados dos gráficos da Visão Geral
  const [nineBoxData, setNineBoxData] = useState([]);
  const [radarData, setRadarData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [scarfData, setScarfData] = useState([]);
  const [alertasData, setAlertasData] = useState([]);

  // Estado de loading genérico para Visão Geral
  const [loading, setLoading] = useState(false);

  // Carregar dados iniciais dos filtros (origens e pessoas)
  useEffect(() => {
    const loadInitialData = async () => {
      const origensData = await apiCall(API_CONFIG.endpoints.origem);
      const pessoasData = await apiCall(API_CONFIG.endpoints.pessoas);

      setOrigens(origensData || []);
      setPessoas(pessoasData || []);
    };

    loadInitialData();
  }, [apiCall]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Carregar dados dos gráficos da Visão Geral quando filtros mudarem
  useEffect(() => {
    const loadDashboardData = async () => {
      if (activeTab !== 'dashboard1') return;

      setLoading(true);

      const params = {};
      if (selectedPessoa) params.pes_id = selectedPessoa;
      if (dataInicio) params.evt_dh_inicio = dataInicio;
      if (dataFim) params.evt_dh_fim = dataFim;

      const [nineBox, radar, heatmap, scarf, alertas] = await Promise.all([
        apiCall(API_CONFIG.endpoints.nineBox, params),
        apiCall(API_CONFIG.endpoints.radarCompetencias, params),
        apiCall(API_CONFIG.endpoints.heatmapCompetencias, params),
        apiCall(API_CONFIG.endpoints.distribuicaoScarf, params),
        apiCall(API_CONFIG.endpoints.alertasCriticos, params)
      ]);

      const processedHeatmapDataMap = heatmap.reduce((acc, current) => {
        if (!acc[current.nome_pessoa]) {
          acc[current.nome_pessoa] = { nome_pessoa: current.nome_pessoa };
        }
        const competenciaKey = current.descricao_caracteristica
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, ' ')
          .replace(/\./g, '')
          .toLowerCase();

        acc[current.nome_pessoa][competenciaKey] = current.valor_medio_analise;
        return acc;
      }, {});

      const newHeatmapData = Object.values(processedHeatmapDataMap);

      setNineBoxData(nineBox || []);
      setRadarData(radar || []);
      setHeatmapData(newHeatmapData || []);
      setScarfData(scarf || []);
      setAlertasData(alertas || []);

      setLoading(false);
    };

    loadDashboardData();
  }, [selectedPessoa, dataInicio, dataFim, activeTab, apiCall]); // eslint-disable-next-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Filtros da Visão Geral */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="origem-filter" className="block text-sm font-medium text-gray-700 mb-2">Origem</label>
            <select
              id="origem-filter"
              value={selectedOrigem}
              onChange={(e) => setSelectedOrigem(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione uma origem</option>
              {origens.map((origem, index) => (
                <option key={index} value={origem}>{origem}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="pessoa-filter" className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
            <select
              id="pessoa-filter"
              value={selectedPessoa}
              onChange={(e) => setSelectedPessoa(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione uma pessoa</option>
              {pessoas.map((pessoa) => (
                <option key={pessoa.pes_id} value={pessoa.pes_id}>{pessoa.pes_nm}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="data-inicio-filter" className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              id="data-inicio-filter"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="data-fim-filter" className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              id="data-fim-filter"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Carregando dados...</p>
        </div>
      )}

      {!loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NineBoxChart nineBoxData={nineBoxData} />
            <RadarCompetenciasChart radarData={radarData} />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <HeatmapChart heatmapData={heatmapData} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DistribuicaoScarfChart scarfData={scarfData} />
            <AlertasCriticosTable alertasData={alertasData} />
          </div>
        </div>
      )}
    </>
  );
};

export default OverviewDashboard;